import React, { useState } from 'react';
import { Button, DropDownButton, DropdownContainer, DropDownMenu, Flex, Icon, Text, Title } from '@upsales/components';
import { useTranslation } from 'Components/Helpers/translate';
import { useDrag, useDrop, useDragLayer } from 'react-dnd';
import BemClass from '@upsales/components/Utils/bemClass';
import DropdownItem from 'App/components/DropdownItem';
import { confirmationText } from '../Helpers/helpers';
import { FolderWithCount } from '../AccountFiles';
import FolderModal from '../FolderModal';
import './Folder.scss';
import logError from 'Helpers/logError';

type Props = {
	id: number;
	name: string;
	openFolder: (folderId: number, name: string) => void;
	deleteFolder: (id: number, totalFiles: number) => void;
	changeFolderFolder: (id: number, folderId: number | null) => void;
	changeFileFolder: (id: number, folderId: number | null) => void;
	folderMap: Map<number, FolderWithCount>;
	changeNameOfFolder: (id: number, name: string) => void;
	numberOfFolders: number;
	numberOfFiles: number;
};

const Folder = ({
	name,
	openFolder,
	id,
	deleteFolder,
	changeFolderFolder,
	changeFileFolder,
	folderMap,
	changeNameOfFolder,
	numberOfFolders,
	numberOfFiles
}: Props) => {
	const classes = new BemClass('Folder');
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const [showRenameModal, setShowRenameModal] = useState(false);
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
	const { t } = useTranslation();
	const folderData = folderMap?.get(id);
	const totalFiles = folderData?.childFiles.length ?? 0;
	const totalFolders = folderData?.totalSubFolderCount ?? 0;

	const lang = {
		irreversibleAction: t('default.thisActionIsIrreversable'),
		deleteFolder: t('file.removeFolder'),
		delete: t('default.delete'),
		removeFolder: t('file.removeFolder'),
		and: t('default.and').toLowerCase(),
		files: t('file.files').toLowerCase(),
		with: t('default.with').toLowerCase(),
		folders: t('file.folders').toLowerCase(),
		folder: t('file.folder').toLowerCase(),
		file: t('file.file').toLowerCase()
	};

	const isDescendant = (folderMap: Map<number, FolderWithCount>, draggedId: number, targetId: number) => {
		let current = folderMap.get(targetId);
		let iterations = 0;
		const maxIterations = 1000;

		while (current && current.folderId !== null && iterations < maxIterations) {
			if (current.folderId === draggedId) {
				return true;
			}
			current = folderMap.get(current.folderId);
			iterations++;
		}

		if (iterations >= maxIterations) {
			logError('Possible circular reference detected in folder hierarchy');
		}

		return false;
	};

	const { isAnyItemDragging, globalDraggedItem } = useDragLayer(monitor => ({
		isAnyItemDragging: monitor.isDragging(),
		globalDraggedItem: monitor.getItem()
	}));

	const canDropHere =
		isAnyItemDragging &&
		globalDraggedItem &&
		(globalDraggedItem.isFile
			? true
			: globalDraggedItem.id !== id && folderMap && !isDescendant(folderMap, globalDraggedItem.id, id));

	const [{ canDropisOver, canDrop }, drop] = useDrop(
		() => ({
			accept: 'ALL',
			canDrop: (item: { id: number; isFile: boolean }) => {
				if (item.isFile) {
					return true;
				} else {
					if (item.id === id) return false;
					if (!folderMap) return true;
					return !isDescendant(folderMap, item.id, id);
				}
			},
			drop: (item: { id: number; isFile: boolean }) => {
				if (item.isFile) {
					changeFileFolder(item.id, id);
				} else if (item.id !== id) {
					changeFolderFolder(item.id, id);
				}
			},
			collect: monitor => ({ canDropisOver: !!monitor.isOver(), canDrop: !!monitor.canDrop() })
		}),
		[numberOfFolders, numberOfFiles]
	);

	const [{ isThisDragging }, drag] = useDrag(
		() => ({
			type: 'ALL',
			canDrag: !isMenuOpen,
			item: { id, isFile: false },
			collect: monitor => ({ isThisDragging: !!monitor.isDragging() })
		}),
		[isMenuOpen]
	);

	const handleDeleteFolder = () => {
		if (totalFiles > 0 || totalFolders > 0) {
			setShowDeleteConfirm(true);
		} else {
			deleteFolder(id, totalFiles);
			setIsMenuOpen(false);
		}
	};

	const CloseDeleteConfirmButton = ({ close }: { close: () => void }) => {
		return (
			<Button
				color="grey"
				type="link"
				className={classes.elem('delete-confirm-close').b()}
				onClick={e => {
					e.stopPropagation();
					setShowDeleteConfirm(false);
					close();
				}}
			>
				<Icon name="times" />
			</Button>
		);
	};

	const ConfirmDeleteButton = ({ close }: { close: () => void }) => {
		return (
			<Button
				block
				color="red"
				onClick={e => {
					e.stopPropagation();
					deleteFolder(id, totalFiles);
					setShowDeleteConfirm(false);
					close();
				}}
			>
				{lang.delete}
			</Button>
		);
	};

	return (
		<div ref={drop}>
			<div
				onClick={() => !isMenuOpen && openFolder(id, name)}
				className={classes
					.mod({
						isThisDragging,
						canDropHere,
						canDropisOver,
						cannotDropHere: !canDropHere && !!globalDraggedItem,
						cannotDropisOver: canDropisOver && !canDrop
					})
					.b()}
				ref={drag}
			>
				<Flex direction="row" justifyContent="space-between" alignItems="center">
					<Flex direction="column" gap="u1" className={classes.elem('folder-name-wrapper').b()}>
						<Text className={classes.elem('folder-name').b()}>{name}</Text>
						<Flex direction="column" gap="0">
							<Text size="sm" color="grey-11" className={classes.elem('folder-count').b()}>
								{totalFolders}{' '}
								{totalFolders === 1 ? t('file.folder').toLowerCase() : t('file.folders').toLowerCase()}
							</Text>
							<Text size="sm" color="grey-11" className={classes.elem('folder-count').b()}>
								{totalFiles}{' '}
								{totalFiles === 1 ? t('file.file').toLowerCase() : t('file.files').toLowerCase()}
							</Text>
						</Flex>
					</Flex>
					<DropDownMenu
						useAnimation={false}
						noMinWidth
						arrowed={false}
						onClose={() => {
							setIsMenuOpen(false);
						}}
						className={classes.elem('dropdown-menu').b()}
						renderTrigger={(isExpanded, setExpanded) => (
							<DropDownButton
								className={classes.elem('menu-button').b()}
								icon="ellipsis-v"
								expanded={isExpanded}
								onClick={(e: Parameters<typeof setExpanded>[0]) => {
									e.stopPropagation();
									setIsMenuOpen(true);
									setShowRenameModal(false);
									setShowDeleteConfirm(false);
									setExpanded(e);
								}}
								hideArrow
								size="sm"
							/>
						)}
					>
						{close => (
							<DropdownContainer
								space={showRenameModal || showDeleteConfirm ? '' : 'ptm prm pbm plm'}
								className={classes.elem('dropdown-container').b()}
							>
								{showRenameModal || showDeleteConfirm ? null : (
									<Flex direction="column">
										<DropdownItem
											icon="edit"
											title={t('file.rename')}
											onClick={e => {
												e.stopPropagation();
												setShowRenameModal(true);
											}}
										/>
										<DropdownItem
											icon="trash"
											title={t('default.delete')}
											onClick={e => {
												e.stopPropagation();
												handleDeleteFolder();
											}}
										/>
									</Flex>
								)}
								{showRenameModal ? (
									<div
										className={classes.elem('rename-modal').b()}
										onClick={e => e.stopPropagation()}
									>
										<FolderModal
											startName={name}
											buttonText={t('file.rename')}
											onClick={name => {
												changeNameOfFolder(id, name);
												setShowRenameModal(false);
												setIsMenuOpen(false);
												close();
											}}
											noDropDown
										/>
									</div>
								) : showDeleteConfirm ? (
									<div
										className={classes.elem('delete-confirm').b()}
										onClick={e => e.stopPropagation()}
									>
										<CloseDeleteConfirmButton close={() => close()} />
										<Title>{confirmationText(totalFolders, totalFiles, name, lang)}</Title>
										<Text space="mbl" color="grey-10">
											{lang.irreversibleAction}
										</Text>
										<ConfirmDeleteButton close={() => close()} />
									</div>
								) : null}
							</DropdownContainer>
						)}
					</DropDownMenu>
				</Flex>
			</div>
		</div>
	);
};

export default Folder;
